import { validateIBAN } from '@/features/clients/validation';
import type { Beneficiary } from '@/features/clients/types';

/**
 * CBS beneficiary response (camelCase, `BeneficiaryResponseDto`) → the
 * snake_case `Beneficiary` view-model the UI renders.
 *
 * Beneficiaries carry no screening status: they are active from creation,
 * with no operator approval step.
 *
 * `clientId` defaults to the id carried on the row. The client detail tab
 * passes its route id explicitly because it lists one client's beneficiaries.
 */
export function mapApiBeneficiary(
  raw: Record<string, unknown>,
  clientId: string = String(raw.clientId ?? ''),
): Beneficiary {
  const accountNumber = (raw.accountNumber as string) ?? '';
  const isIban = validateIBAN(accountNumber);
  return {
    id: String(raw.beneficiaryId ?? ''),
    client_id: clientId,
    client_name: (raw.clientName as string | null) ?? null,
    beneficiary_type_id: (raw.beneficiaryTypeId as number | null) ?? null,
    beneficiary_name: (raw.label as string) ?? (raw.companyName as string) ?? '',
    company_name: (raw.companyName as string | null) ?? null,
    first_name: (raw.firstName as string | null) ?? null,
    last_name: (raw.lastName as string | null) ?? null,
    account_number: accountNumber,
    iban: isIban ? accountNumber : null,
    bic: (raw.bankCode as string | null) ?? null,
    country_code: (raw.bankCountryCode as string) ?? (raw.addressCountryCode as string) ?? '',
    bank_name: (raw.bankName as string | null) ?? null,
    bank_country_code: (raw.bankCountryCode as string | null) ?? null,
    bank_code: (raw.bankCode as string | null) ?? null,
    currency_code: (raw.currencyCode as string | null) ?? null,
    bank_network_id: (raw.bankNetworkId as number | null) ?? null,
    address_line1: (raw.addressLine1 as string | null) ?? null,
    address_line2: (raw.addressLine2 as string | null) ?? null,
    address_city: (raw.addressCity as string | null) ?? null,
    address_state: (raw.addressState as string | null) ?? null,
    address_zip: (raw.addressZip as string | null) ?? null,
    address_country_code: (raw.addressCountryCode as string | null) ?? null,
    // Payment Formatting Guide fields. Copied as-is: these are strings, so
    // leading zeroes and the bare YYYY-MM-DD birth date stay untouched.
    tax_id: (raw.taxId as string | null) ?? null,
    email: (raw.email as string | null) ?? null,
    phone_number: (raw.phoneNumber as string | null) ?? null,
    date_of_birth: (raw.dateOfBirth as string | null) ?? null,
    bank_branch_code: (raw.bankBranchCode as string | null) ?? null,
    bank_account_type: (raw.bankAccountType as string | null) ?? null,
    bank_branch_name: (raw.bankBranchName as string | null) ?? null,
    bank_address_line1: (raw.bankAddressLine1 as string | null) ?? null,
    bank_address_line2: (raw.bankAddressLine2 as string | null) ?? null,
    bank_address_line3: (raw.bankAddressLine3 as string | null) ?? null,
    bank_address_city: (raw.bankAddressCity as string | null) ?? null,
    bank_address_state: (raw.bankAddressState as string | null) ?? null,
    bank_address_zip: (raw.bankAddressZip as string | null) ?? null,
    is_active: Boolean(raw.isActive),
    // `createdAt` only reached this response once the pending queue needed it;
    // rows served by an older backend still fall back to the update stamp.
    created_at: (raw.createdAt as string) ?? (raw.updatedAt as string) ?? '',
  };
}
